import type { ClientAnalyticsConfig } from "./config";
import {
  consentCookieString,
  consentStatus,
  CONSENT_COOKIE,
  cookieDeletionStrings,
  decodeConsent,
  gaCookieNames,
  readCookie,
  type ConsentStatus,
} from "./consent";
import { cleanPageLocation, compactParams, contentGroupFor, type EventMap, type EventName } from "./events";

// Tarayıcı tarafı analitik çalışma zamanı (yalnızca istemci bileşenleri içe aktarır). Onay yoksa hiçbir şey yapmaz:
// gtag.js yüklenmez, olay kuyruğa yazılmaz, Google'a istek gitmez (Consent Mode v2 "temel" uygulama). Onay verilince
// gtag önce tümü "denied" varsayılanıyla, ardından analytics_storage "granted" güncellemesiyle kurulur; reklam sinyalleri
// (ad_storage, ad_user_data, ad_personalization) her zaman "denied". Ayrıntı: docs/analytics.md

type Gtag = (...args: unknown[]) => void;
type LogEntry = { name: string; params: Record<string, unknown> };
declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: Gtag;
    /** Hata ayıklama / kuru çalıştırma: gönderilen olaylar (testler okur) */
    __fxAnalytics?: LogEntry[];
    [gaDisable: `ga-disable-${string}`]: boolean | undefined;
  }
}

/** GA çerezlerinin ömrü: 13 ay (varsayılan 2 yıl yerine; AB denetim makamlarının ölçüm çerezi önerisi) */
const GA_COOKIE_EXPIRES = 395 * 24 * 60 * 60;
/** Yeni sayfanın başlığı (akışla gelen metadata) en fazla bu kadar beklenir */
const TITLE_WAIT_MS = 1000;
/** Ana sayfa aramasındaki tur kodunun sonucu (tur sayfası açıldı mı) bu süre içinde eşleştirilir */
const CODE_SEARCH_TTL_MS = 30_000;

export type AnalyticsState = {
  /** Onay çerezi okundu mu (sunucuda ve ilk çizimde false: bant hidrasyondan sonra çıkar, yerleşim kaymaz) */
  ready: boolean;
  consent: ConsentStatus;
  /** gtag.js etiketi eklensin mi (onay + gerçek kimlik) */
  loadScript: boolean;
  preferencesOpen: boolean;
};

const SERVER_STATE: AnalyticsState = { ready: false, consent: "unknown", loadScript: false, preferencesOpen: false };
let state: AnalyticsState = SERVER_STATE;
let config: ClientAnalyticsConfig | null = null;
const listeners = new Set<() => void>();

function setState(patch: Partial<AnalyticsState>) {
  state = { ...state, ...patch };
  for (const l of listeners) l();
}

export const subscribe = (fn: () => void) => {
  listeners.add(fn);
  return () => void listeners.delete(fn);
};
export const getState = () => state;
export const getServerState = () => SERVER_STATE;

// --- Kurulum ve onay ---

/** Kök düzendeki AnalyticsRoot çağırır: yapılandırma + kayıtlı onay. Onay yoksa eski GA çerezleri temizlenir. */
export function init(next: ClientAnalyticsConfig) {
  const localeChanged = config !== null && config.locale !== next.locale;
  config = next;
  if (state.ready) {
    if (localeChanged && state.consent === "granted") setUserProperties();
    return;
  }
  const record = decodeConsent(readCookie(document.cookie, CONSENT_COOKIE));
  const consent = consentStatus(record, { version: next.policyVersion, months: next.consentMonths, now: Date.now() / 1000 });
  if (consent !== "granted") deleteGaCookies();
  setState({ ready: true, consent });
  if (consent === "granted") {
    bootstrap();
    // İzleyicinin ilk çağrısı yapılandırmadan önce geldi (alt bileşen etkileri önce çalışır): ilk sayfa burada
    pageView();
  }
}

/** Kullanıcının seçimi: onay çerezini yazar; izin → gtag kurulur ve bu sayfanın görüntülemesi gönderilir; ret/geri alma → durdurulur, GA çerezleri silinir */
export function decide(analytics: boolean) {
  if (!config) return;
  const record = { version: config.policyVersion, analytics, decidedAt: Math.floor(Date.now() / 1000) };
  document.cookie = consentCookieString(record, { months: config.consentMonths, secure: location.protocol === "https:" });
  const was = state.consent;
  setState({ consent: analytics ? "granted" : "denied", preferencesOpen: false });
  if (analytics) {
    bootstrap();
    if (was !== "granted") pageView();
  } else {
    stop();
  }
}

export const openPreferences = () => setState({ preferencesOpen: true });
export const closePreferences = () => setState({ preferencesOpen: false });

function debugLog(name: string, params: Record<string, unknown>) {
  if (!config || !(config.debug || config.dryRun)) return;
  (window.__fxAnalytics ??= []).push({ name, params });
  console.info(`[analitik${config.dryRun ? " · kuru" : ""}] ${name}`, params);
}

let bootstrapped = false;

/** gtag kuyruğu ve yapılandırma (gtag.js henüz yüklenmeden; komutlar dataLayer'da bekler) */
function bootstrap() {
  if (!config || config.dryRun || !config.measurementId) return;
  const id = config.measurementId;
  window[`ga-disable-${id}`] = false;
  if (bootstrapped) {
    window.gtag?.("consent", "update", { analytics_storage: "granted" });
    return;
  }
  bootstrapped = true;
  window.dataLayer = window.dataLayer ?? [];
  // gtag.js "arguments" nesnesi bekler (dizi değil)
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments);
  };
  const gtag = window.gtag;
  gtag("consent", "default", {
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    analytics_storage: "denied",
    functionality_storage: "denied",
    personalization_storage: "denied",
    security_storage: "granted",
  });
  gtag("consent", "update", { analytics_storage: "granted" });
  gtag("set", "ads_data_redaction", true);
  gtag("set", "url_passthrough", false);
  gtag("js", new Date());
  gtag("config", id, {
    // Sayfa görüntülemeleri elle (istemci gezintisi başına bir kez: pageView); GA arayüzünde "tarayıcı geçmişi
    // olaylarına göre sayfa değişiklikleri" kapatılmalı (docs/analytics.md → kurulum)
    send_page_view: false,
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
    cookie_expires: GA_COOKIE_EXPIRES,
    ...(location.protocol === "https:" ? { cookie_flags: "SameSite=Lax;Secure" } : {}),
    // debug_mode false olarak da gönderilirse GA yine hata ayıklama sayar: yalnızca açıkken eklenir
    ...(config.debug ? { debug_mode: true } : {}),
  });
  setUserProperties();
  setState({ loadScript: true });
}

function setUserProperties() {
  if (!config) return;
  debugLog("user_properties", { site_language: config.locale });
  window.gtag?.("set", "user_properties", { site_language: config.locale });
}

/** Onay geri alındı / reddedildi: gönderim durur (ga-disable + consent denied), GA çerezleri silinir */
function stop() {
  if (config?.measurementId) {
    window[`ga-disable-${config.measurementId}`] = true;
    if (bootstrapped) window.gtag?.("consent", "update", { analytics_storage: "denied" });
  }
  deleteGaCookies();
  view.sent = false;
}

function deleteGaCookies() {
  for (const name of gaCookieNames(document.cookie)) {
    for (const c of cookieDeletionStrings(name, location.hostname)) document.cookie = c;
  }
}

// --- Olay gönderimi ---

function send(name: string, params: Record<string, unknown>) {
  if (!config || state.consent !== "granted") return;
  const clean = compactParams(params);
  debugLog(name, clean);
  if (config.dryRun || !config.measurementId) return;
  window.gtag?.("event", name, clean);
}

/** Olay gönderir (yalnızca analitik onayı varsa; yoksa hiçbir şey yapılmaz, kuyruğa da yazılmaz) */
export function track<N extends Exclude<EventName, "page_view">>(name: N, params: EventMap[N]) {
  send(name, params as Record<string, unknown>);
}

export const isTracking = () => state.consent === "granted" && config !== null;

// --- Sayfa görüntüleme ---

type View = { key: string; sent: boolean; fired: Set<string>; queue: Map<string, () => void> };
let view: View = { key: "", sent: false, fired: new Set(), queue: new Map() };
let lastLocation: string | null = null;
let lastTitle: string | null = null;
let pending: { cancel: () => void; flush: () => void } | null = null;

const locationKey = () => location.pathname + location.search;

function ensureView(): View {
  const key = locationKey();
  if (view.key !== key) view = { key, sent: false, fired: new Set(), queue: new Map() };
  return view;
}

/**
 * Bu adresin sayfa görüntülemesi (istemci gezintisi başına bir kez; aynı adres tekrar sayılmaz). Yeni sayfanın başlığı
 * akışla gelebildiği için başlık değişene kadar (en fazla 1 sn) beklenir; aynı yolda yalnızca sorgu değiştiyse beklenmez.
 */
export function pageView() {
  if (!config || state.consent !== "granted") return;
  pending?.flush();
  const v = ensureView();
  if (v.sent) return;
  v.sent = true;
  const href = location.href;
  const samePath = lastLocation !== null && new URL(lastLocation).pathname === location.pathname;
  let done = false;
  let observer: MutationObserver | null = null;
  let timer = 0;
  const fire = () => {
    if (done) return;
    done = true;
    observer?.disconnect();
    window.clearTimeout(timer);
    pending = null;
    const pageLocation = cleanPageLocation(href);
    const marker = document.querySelector("[data-ga-group]")?.getAttribute("data-ga-group");
    send("page_view", {
      page_location: pageLocation,
      page_title: document.title.slice(0, 300),
      ...(lastLocation ? { page_referrer: lastLocation } : {}),
      language: config?.locale ?? "tr",
      content_group: contentGroupFor(new URL(href).pathname, marker),
    } satisfies EventMap["page_view"]);
    lastLocation = pageLocation;
    lastTitle = document.title;
    // Bu sayfanın bekleyen olayları (view_item, view_item_list …) sayfa görüntülemesinden sonra
    for (const [key, fn] of v.queue) {
      if (v.fired.has(key)) continue;
      v.fired.add(key);
      fn();
    }
    v.queue.clear();
  };
  pending = { cancel: () => ((done = true), observer?.disconnect(), window.clearTimeout(timer)), flush: fire };
  if (lastTitle === null || samePath || document.title !== lastTitle) {
    timer = window.setTimeout(fire, 0);
    return;
  }
  observer = new MutationObserver(() => document.title !== lastTitle && fire());
  observer.observe(document.head, { subtree: true, childList: true, characterData: true });
  timer = window.setTimeout(fire, TITLE_WAIT_MS);
}

/**
 * Bu sayfaya ait bir olay (sayfa görüntülemesinden sonra, sayfa başına bir kez; `key` aynı sayfadaki tekrarı önler).
 * Onay sonradan verilirse o anda gönderilir. Dönen fonksiyon bileşen kalkınca bekleyen olayı iptal eder.
 */
export function onPage(key: string, fn: () => void): () => void {
  const v = ensureView();
  if (v.fired.has(key)) return () => {};
  if (v.sent && isTracking() && !pending) {
    v.fired.add(key);
    fn();
    return () => {};
  }
  v.queue.set(key, fn);
  return () => {
    if (v.queue.get(key) === fn) v.queue.delete(key);
  };
}

// --- Tur kodu araması ---

let codeSearch: { code: string; at: number } | null = null;

/** Ana sayfa / filtre panelinde kodla arandı: sonuç (tur sayfası, kalkış, liste) bir sonraki sayfada belli olur */
export function rememberCodeSearch(code: string) {
  codeSearch = { code, at: Date.now() };
}

/** Bekleyen kod araması (bir kez okunur) */
export function takeCodeSearch(): string | null {
  const c = codeSearch;
  codeSearch = null;
  return c && Date.now() - c.at < CODE_SEARCH_TTL_MS ? c.code : null;
}

/** Testler için: modül durumunu sıfırlar */
export function __resetForTests() {
  state = SERVER_STATE;
  config = null;
  bootstrapped = false;
  view = { key: "", sent: false, fired: new Set(), queue: new Map() };
  lastLocation = null;
  lastTitle = null;
  pending?.cancel();
  pending = null;
  codeSearch = null;
}

export const currentConfig = () => config;
